import { confirmSignIn, getUserMfaOptions, sendMFASelection } from '/b2b/authentication/services';
import {
    getMfaOptions as getMfaOptionsSelector,
    getPhone,
    isPhoneVerified as isPhoneVerifiedSelector,
} from '/b2b/authentication/state/Auth.selectors';

import { AUTH_STATES } from '/b2b/authentication/constants';
import AmplifyActions from '/b2b/authentication/state/amplify/Amplify.actions';
import AuthActions from '/b2b/authentication/state/Auth.actions';
import { DEFAULT_MFA_OPTIONS } from '/b2b/authentication/state/amplify/Amplify.constants';
import { getSignInStep } from '/b2b/authentication/state/amplify/Amplify.selectors';

export const getMfaOptions = () => dispatch => {
    dispatch(AuthActions.getMfaOptionsBegin());

    return getUserMfaOptions()
        .then(mfaOptions => {
            dispatch(AuthActions.getMfaOptionsSuccess(undefined, mfaOptions.enabled));
            return mfaOptions.enabled;
        })
        .catch(error => {
            dispatch(AuthActions.getMfaOptionsFailure(undefined, error));
            return DEFAULT_MFA_OPTIONS;
        });
};

export const selectMFA = mfaType => (dispatch, getState) => {
    const state = getState();
    const savedMFAOptions = getMfaOptionsSelector(state);
    const isSMSEnabled = savedMFAOptions.includes('SMS');
    const isTOTPEnabled = savedMFAOptions.includes('TOTP');
    const isPhoneVerified = isPhoneVerifiedSelector(state);
    const hasPhone = !!getPhone(state);

    // We can test against the Amplify signInStep if MFA has already been setup
    const signInStep = getSignInStep(state);

    dispatch(AuthActions.selectMfa(mfaType));

    switch (signInStep) {
        case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
        case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE': {
            // We already selected an MFA, and now we are (maybe) changing our minds
            if (
                (signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE' && mfaType === 'TOTP') ||
                (signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE' && mfaType === 'SMS')
            ) {
                // User reselected the same MFA method, no harm
                dispatch(AuthActions.updateAuthState(AUTH_STATES.confirmSignIn));
                return Promise.resolve(mfaType);
            }
            return sendMFASelection(mfaType).then(() => {
                dispatch(AuthActions.updateAuthState(AUTH_STATES.confirmSignIn));
                return mfaType;
            });
        }
        case 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION': {
            return confirmSignIn({ challengeResponse: mfaType }).then(signInOutput => {
                dispatch(AmplifyActions.nextStep(signInOutput));
                return mfaType;
            });
        }
        case 'CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION': {
            return confirmSignIn({ challengeResponse: mfaType }).then(signInOutput => {
                dispatch(AmplifyActions.nextStep(signInOutput));
                return mfaType;
            });
        }
    }

    // Everything below this line is for new users who have not yet setup any MFA methods, because the sign in step is `DONE`

    // Determine if we need more verifications
    switch (mfaType) {
        case 'SMS': {
            // We need to see if the user has entered a verified phone number
            if (hasPhone) {
                if (isSMSEnabled) {
                    dispatch(AuthActions.updateAuthState(AUTH_STATES.confirmSignIn));
                } else if (isPhoneVerified) {
                    /*
                     * We are in an odd state where the user has a verified phone
                     * but SMS is not enabled. We cannot proceed with SMS MFA because
                     * it's not been added to the account, and we should NOT add it
                     * until the user has explicitly confirmed that they possess the
                     * phone number associated with the account.
                     *
                     * Therefore, we have to verify the phone AGAIN, but we do it from
                     * the SignInConfirm component.
                     */
                    dispatch(AuthActions.updateAuthState(AUTH_STATES.confirmSignIn));
                } else {
                    dispatch(AuthActions.updateAuthState(AUTH_STATES.verifyPhone));
                }
            } else {
                dispatch(AuthActions.updateAuthState(AUTH_STATES.setupPhone));
            }
            break;
        }
        case 'TOTP': {
            if (isTOTPEnabled) {
                // TODO: Don't force a user to scan a new code
                dispatch(AuthActions.updateAuthState(AUTH_STATES.verifyTOTP));
            } else {
                dispatch(AuthActions.updateAuthState(AUTH_STATES.setupTOTP));
            }
            break;
        }
    }

    return Promise.resolve(mfaType);
};
